import React, { createContext, useContext, useEffect, useState } from 'react';
import { type User, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { authService } from '../services/auth';
import type { Staff } from '../types';

interface AuthContextType {
    currentUser: User | null;
    currentStaff: Staff | null;
    isAdmin: boolean;
    canDelete: boolean;
    canImport: boolean;
    loading: boolean;
}

const AuthContext = createContext<AuthContextType>({
    currentUser: null,
    currentStaff: null,
    isAdmin: false,
    canDelete: false,
    canImport: false,
    loading: true,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [currentStaff, setCurrentStaff] = useState<Staff | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setCurrentUser(user);
            if (user?.email) {
                try {
                    // Call /auth/me endpoint which triggers auto-provisioning if user doesn't exist
                    const staff = await authService.getCurrentUser();
                    setCurrentStaff(staff);
                } catch (error) {
                    console.error('Failed to fetch staff details:', error);
                    setCurrentStaff(null);
                }
            } else {
                setCurrentStaff(null);
            }
            setLoading(false);
        });

        return unsubscribe;
    }, []);

    const role = currentStaff?.role || 'User';
    const isOwner = role === 'Admin'; // Keep isAdmin for backward compat if needed, or deprecate
    const canDelete = role === 'Admin';
    const canImport = role === 'Admin' || role === 'Editor';

    const value = {
        currentUser,
        currentStaff,
        isAdmin: isOwner,
        canDelete,
        canImport,
        loading
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};
